import { addDays, format, getISODay, parseISO } from 'date-fns'
import range from 'lodash-es/range'
import uniqBy from 'lodash-es/uniqBy'

import { EMPTY_LIST } from '../../constants/common'
import { DATE_API_DATA_FORMAT_SHORT } from '../../constants/date'
import { sleep } from '../../utils/async'
import { getUserFullName } from '../../utils/user'

const CALENDAR_DAY_LOAD_MOCK_LATENCY = 400

const CALENDAR_DAY_LOAD_MOCK_LIMIT = 3

const CALENDAR_DAY_LOAD_MOCK_NEXT_FREE_DAY_SEARCH = 30

const CALENDAR_DAY_LOAD_MOCK_REGULAR_COUNT = 1

const CALENDAR_DAY_LOAD_MOCK_WEEKDAY_COUNTS = { 2: 2, 3: 3 }

function getCalendarDayLoadMockCount(day) {
  return (
    CALENDAR_DAY_LOAD_MOCK_WEEKDAY_COUNTS[getISODay(day)] ??
    CALENDAR_DAY_LOAD_MOCK_REGULAR_COUNT
  )
}

function getIsCalendarDayLoadMockFull(count) {
  return count >= CALENDAR_DAY_LOAD_MOCK_LIMIT
}

function getCalendarDayLoadMockNextFreeDate(date) {
  const nextFreeDay = range(CALENDAR_DAY_LOAD_MOCK_NEXT_FREE_DAY_SEARCH)
    .map(offset => addDays(parseISO(date), offset))
    .find(
      day => !getIsCalendarDayLoadMockFull(getCalendarDayLoadMockCount(day))
    )

  return nextFreeDay ? format(nextFreeDay, DATE_API_DATA_FORMAT_SHORT) : null
}

function getCalendarDayLoadMockAssignees(requests) {
  const assignees = requests.flatMap(request => request.assignees ?? EMPTY_LIST)

  return uniqBy(assignees, 'id').map(person => ({
    person,
    requestCount: assignees.filter(assignee => assignee.id === person.id)
      .length,
  }))
}

function toCalendarDayLoadMockUserLoad({ date, person, requestCount }) {
  const followUpCount = getCalendarDayLoadMockCount(parseISO(date))

  const followUpCountAfter = followUpCount + requestCount

  return {
    days: [
      {
        date,
        follow_up_count: followUpCount,
        follow_up_count_after: followUpCountAfter,
        is_full: getIsCalendarDayLoadMockFull(followUpCount),
        is_full_after: getIsCalendarDayLoadMockFull(followUpCountAfter),
      },
    ],
    name: getUserFullName(person),
    next_free_date: getCalendarDayLoadMockNextFreeDate(date),
    user_id: person.id,
  }
}

function toCalendarDayLoadMockAdvisoryLoad({ date, person, requestCount }) {
  const followUpCount =
    getCalendarDayLoadMockCount(parseISO(date)) + requestCount

  return {
    follow_up_count: followUpCount,
    is_full: getIsCalendarDayLoadMockFull(followUpCount),
    name: getUserFullName(person),
    next_free_date: getCalendarDayLoadMockNextFreeDate(date),
    user_id: person.id,
  }
}

// TODO: drop the mock once the backend serves the day load (TM-01-12) and
// returns day_load_advisory with each due-date write and background run
// (TM-01-13, TM-01-14, TM-01-48); the server finds the assignees itself.
export function getMockCalendarDayLoad({ date, requests }) {
  return sleep(CALENDAR_DAY_LOAD_MOCK_LATENCY).then(() => ({
    data: {
      limit: CALENDAR_DAY_LOAD_MOCK_LIMIT,
      per_user: getCalendarDayLoadMockAssignees(requests).map(assignee =>
        toCalendarDayLoadMockUserLoad({ ...assignee, date })
      ),
    },
  }))
}

export function getMockCalendarDayLoadAdvisory({ date, requests }) {
  return {
    date,
    limit: CALENDAR_DAY_LOAD_MOCK_LIMIT,
    per_user: getCalendarDayLoadMockAssignees(requests).map(assignee =>
      toCalendarDayLoadMockAdvisoryLoad({ ...assignee, date })
    ),
  }
}
